import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, TrendingUp, Calendar, Users, CheckCircle, ArrowRight, Target, Calculator } from 'lucide-react';
import AvoirsLPP from '../components/AvoirsLPP';
import SEO, { SEOPages } from '../components/SEO';

const Retraite = () => {
  const pillarData = [
    {
      pillar: "1er pilier",
      title: "AVS/AI - Prévoyance étatique",
      description: "Rente de base garantie par l'État",
      maxRent: "CHF 2'520.-/mois",
      color: "from-blue-400 to-blue-600",
      features: ["Obligatoire pour tous", "Rente de base", "Indexée sur l'inflation"]
    },
    {
      pillar: "2e pilier", 
      title: "LPP - Prévoyance professionnelle",
      description: "Caisse de pension liée à l'employeur",
      maxRent: "Selon la caisse de pension",
      color: "from-green-400 to-green-600",
      features: ["Obligatoire dès CHF 22'680", "Financement paritaire", "Rachat possible"]
    },
    {
      pillar: "3e pilier",
      title: "Prévoyance privée individuelle",
      description: "Épargne personnelle avec avantages fiscaux",
      maxRent: "Selon épargne",
      color: "from-purple-400 to-purple-600",
      features: ["Volontaire", "Déduction fiscale", "Flexible"]
    }
  ];

  const retirementStrategies = [
    {
      icon: Target,
      title: "Planification personnalisée",
      description: "Analyse de vos objectifs de retraite et création d'une stratégie sur mesure"
    },
    {
      icon: TrendingUp,
      title: "Optimisation 3e pilier",
      description: "Maximisation des avantages fiscaux et du rendement de votre épargne"
    },
    {
      icon: Shield,
      title: "Rachats LPP optimisés",
      description: "Stratégie de rachat pour combler les lacunes et réduire les impôts"
    },
    {
      icon: Calendar,
      title: "Planification de retrait",
      description: "Timing optimal pour les retraits de vos avoirs de prévoyance"
    }
  ];

  const ageScenarios = [
    { age: "20-35 ans", focus: "Construction", advice: "Démarrer le 3e pilier, optimiser les déductions" },
    { age: "35-45 ans", focus: "Acceleration", advice: "Rachats LPP, augmentation 3e pilier" },
    { age: "45-55 ans", focus: "Optimisation", advice: "Stratégie de retrait, planification fiscale" },
    { age: "55-65 ans", focus: "Finalisation", advice: "Préparation retraite, optimisation finale" }
  ];

  return (
    <div className="pt-20">
      <SEO {...SEOPages.retraite} />
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          role="img"
          aria-label="Planification retraite Suisse - 3ème pilier, LPP et prévoyance anticipée"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1514415008039-efa173293080?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxyZXRpcmVtZW50JTIwcGxhbm5pbmd8ZW58MHx8fHwxNzYwNTMwNzY5fDA&ixlib=rb-4.1.0&q=85)',
            backgroundBlendMode: 'overlay'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40"></div>
        </div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold heading-display text-white mb-6">
              Retraite : préparez votre <span className="text-purple-300">avenir</span> sereinement
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Optimisation des 3 piliers suisses pour une retraite confortable. 
              Notre expertise vous accompagne à chaque étape de votre vie professionnelle.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/contact" className="btn-glass btn-primary text-lg px-8 py-4 flex items-center justify-center">
                Analyser ma retraite
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link to="/contact" className="btn-glass text-lg px-8 py-4 flex items-center justify-center">
                <Calculator className="mr-2" size={20} />
                Demander une offre
              </Link>
            </div>

            {/* Statistiques retraite */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-purple-400 mb-2">60 %</div>
                <div className="text-gray-300">Dernier salaire couvert en moyenne par l'AVS et la LPP</div>
              </div>
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-blue-400 mb-2">CHF 7'258</div>
                <div className="text-gray-300">Montant maximum déductible chaque année au 3ᵉ pilier A pour un salarié</div>
              </div>
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-white mb-2">2.6 mio</div>
                <div className="text-gray-300">Bénéficiaires de rentes AVS en Suisse (déc. 2024)</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Les 3 piliers */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Comprendre les 3 piliers suisses
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Un système à trois niveaux pour assurer votre sécurité financière à la retraite
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {pillarData.map((pillar, index) => (
              <div key={index} className="glass p-8 group">
                <div className={`w-16 h-16 bg-gradient-to-br ${pillar.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <span className="text-2xl font-bold text-white">{index + 1}</span>
                </div>
                
                <div className="mb-4">
                  <div className="text-sm text-gray-400 uppercase tracking-wide">{pillar.pillar}</div>
                  <h3 className="text-xl font-semibold text-white mb-2">{pillar.title}</h3>
                  <p className="text-gray-300 mb-4">{pillar.description}</p>
                </div>
                
                <div className="glass p-4 rounded-xl mb-6">
                  <div className="text-sm text-gray-300">
                    {index === 2 ? 'Capital maximal' : 'Rente maximale'}
                  </div>
                  <div className="text-lg font-bold text-white">{pillar.maxRent}</div>
                </div>
                
                <ul className="space-y-2">
                  {pillar.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-300">
                      <CheckCircle size={16} className="text-green-400 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stratégies par âge */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Stratégie selon votre âge
            </h2>
            <p className="text-xl text-gray-300">
              Chaque période de la vie nécessite une approche spécifique
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ageScenarios.map((scenario, index) => (
              <div key={index} className="glass p-6 rounded-2xl text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Users size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{scenario.age}</h3>
                <div className="text-sm text-purple-300 mb-3 uppercase tracking-wide">{scenario.focus}</div>
                <p className="text-gray-300 text-sm">{scenario.advice}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos services retraite */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Nos services retraite
            </h2>
            <p className="text-xl text-gray-300">
              Un accompagnement complet pour optimiser votre prévoyance
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {retirementStrategies.map((strategy, index) => {
              const IconComponent = strategy.icon;
              return (
                <div key={index} className="glass p-8 group">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-4">{strategy.title}</h3>
                  <p className="text-gray-300">{strategy.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Retrouver mes avoirs LPP */}
      <AvoirsLPP />

      {/* CTA Section */}
      <section className="py-20">
        <div className="container">
          <div className="glass p-12 rounded-3xl text-center">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Commencez dès aujourd'hui à préparer votre retraite
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Plus vous commencez tôt, plus vous bénéficiez de la puissance des intérêts composés. 
              Chaque année compte pour votre futur confort de vie.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-glass btn-primary text-lg px-12 py-4">
                Obtenir un comparatif de 3ème pilier
              </Link>
              <Link to="/contact" className="btn-glass text-lg px-8 py-4">
                Demander un conseil
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Retraite;